import { createSlice } from "@reduxjs/toolkit";
import {
  getConfirmedWaterLevelCm,
  resolveTelemetryDisplay,
} from "../../Utils/lidarTelemetryFields";
import {
  getRainfallCumulativeMm,
  getRainfallIntensityMmPerMin,
  RAIN_ACTIVE_INTENSITY_MM_PER_MIN,
  resolveSensorTelemetryDisplay,
} from "../../Utils/sensorTelemetryFields";

const INITIAL_STATE = {
  liveByLocationId: {},
};

const MAX_RECENT_READINGS = 60;

function appendRecentReading(history = [], entry) {
  if (!entry?.t || entry.v == null) return history;
  const key = `${entry.t}|${entry.v}`;
  const next = [
    entry,
    ...history.filter((point) => `${point.t}|${point.v}` !== key),
  ];
  return next.slice(0, MAX_RECENT_READINGS);
}

function isNewerReading(incomingAt, existingAt) {
  if (!incomingAt) return true;
  if (!existingAt) return true;
  return new Date(incomingAt).getTime() >= new Date(existingAt).getTime();
}

function maxTelemetryTimestamp(...values) {
  let maxMs = 0;
  let maxValue = null;
  for (const value of values) {
    if (!value) continue;
    const ms = new Date(value).getTime();
    if (Number.isFinite(ms) && ms >= maxMs) {
      maxMs = ms;
      maxValue = value;
    }
  }
  return maxValue;
}

const telemetryLiveSlice = createSlice({
  name: "telemetryLive",
  initialState: INITIAL_STATE,
  reducers: {
    updateLidarTelemetryLive(state, action) {
      const payload = action.payload;
      if (!payload?.locationId) return;

      const display = resolveTelemetryDisplay(payload);
      const wl = getConfirmedWaterLevelCm(display);
      if (wl == null) return;

      const receivedAt = payload.receivedAt ?? payload.timestamp ?? null;
      const locationId = String(payload.locationId);
      const existing = state.liveByLocationId[locationId] ?? {};

      if (!isNewerReading(receivedAt, existing.wlReceivedAt)) return;

      state.liveByLocationId[locationId] = {
        ...existing,
        WL: wl,
        wlLastUpdated: receivedAt,
        wlReceivedAt: receivedAt,
        recentWl: appendRecentReading(existing.recentWl, {
          t: receivedAt,
          v: wl,
        }),
        telemetryLastUpdated: maxTelemetryTimestamp(
          receivedAt,
          existing.rainLastUpdated,
          existing.telemetryLastUpdated,
        ),
      };
    },
    updateSensorTelemetryLive(state, action) {
      const payload = action.payload;
      if (!payload?.locationId) return;

      const display = resolveSensorTelemetryDisplay(payload);
      // Location list rain must follow master sensor — slave ticks were overwriting rain.
      const role = String(display.deviceRole ?? payload.deviceRole ?? "")
        .trim()
        .toLowerCase();
      if (role === "slave") return;

      const rainInMM = getRainfallCumulativeMm(display);
      const rainIntensity = getRainfallIntensityMmPerMin(display);
      const confirmed =
        display?.confirmedLevelAvgCm ?? display?.confirmed_level_avg_cm;
      const wl =
        confirmed === 0 || confirmed === "0"
          ? 0
          : confirmed == null || confirmed === ""
            ? null
            : Number.isFinite(Number(confirmed))
              ? Number(confirmed)
              : null;

      const receivedAt = payload.receivedAt ?? payload.timestamp ?? null;
      const locationId = String(payload.locationId);
      const existing = state.liveByLocationId[locationId] ?? {};
      const next = { ...existing };

      // Combined sensor payload carries confirmed WL — keep location/dashboard live
      // in sync without depending only on lidar fan-out WS.
      if (wl != null && isNewerReading(receivedAt, existing.wlReceivedAt)) {
        next.WL = wl;
        next.wlLastUpdated = receivedAt;
        next.wlReceivedAt = receivedAt;
        next.recentWl = appendRecentReading(existing.recentWl, {
          t: receivedAt,
          v: wl,
        });
      }

      // Same MQTT packet as WL: always advance rain "Last Data At" on master sensor
      // ticks (even when intensity/cumulative are null) so Jul rain can't stick.
      if (isNewerReading(receivedAt, existing.rainReceivedAt)) {
        next.rainLastUpdated = receivedAt;
        next.rainReceivedAt = receivedAt;
        next.rainInMM = rainInMM;
        next.rainIntensityMmPerMin = rainIntensity;
        next.isRaining =
          rainIntensity != null &&
          rainIntensity > RAIN_ACTIVE_INTENSITY_MM_PER_MIN;

        if (rainInMM != null) {
          next.recentRainCumulative = appendRecentReading(
            existing.recentRainCumulative,
            { t: receivedAt, v: rainInMM },
          );
        }
        if (rainIntensity != null) {
          next.recentRainIntensity = appendRecentReading(
            existing.recentRainIntensity,
            { t: receivedAt, v: rainIntensity },
          );
        }
      }

      next.telemetryLastUpdated = maxTelemetryTimestamp(
        receivedAt,
        next.wlLastUpdated,
        next.rainLastUpdated,
        existing.telemetryLastUpdated,
      );

      state.liveByLocationId[locationId] = next;
    },
    clearTelemetryLive(state) {
      state.liveByLocationId = {};
    },
  },
});

export const {
  updateLidarTelemetryLive,
  updateSensorTelemetryLive,
  clearTelemetryLive,
} = telemetryLiveSlice.actions;
export default telemetryLiveSlice.reducer;
